// Dependency-light branded recovery UI. Renders even if app data, auth,
// trip state, route loaders, native bridge state, or Supabase queries are
// broken. Pure presentational + window APIs only — do NOT import contexts,
// hooks tied to auth/router state, or anything that could throw.
import { useTranslation } from "react-i18next";
import { clearBootRecoveryGuard, requestCacheBustingReload } from "@/lib/boot-recovery";

export interface RoavaRecoveryUIProps {
  onRetry?: () => void;
  title?: string;
  body?: string;
}

export function RoavaRecoveryUI({
  onRetry,
  title,
  body,
}: RoavaRecoveryUIProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("shell.recovery_title");
  const resolvedBody = body ?? t("shell.recovery_body");
  const handleRefresh = () => {
    try {
      onRetry?.();
    } catch {
      /* fall through to reload */
    }
    // Always escalate to a cache-busting reload — onRetry may have only
    // cleared an in-memory boundary without re-fetching shell assets.
    requestCacheBustingReload();
  };

  const handleHome = () => {
    clearBootRecoveryGuard();
    try {
      window.location.href = "/";
    } catch {
      /* noop */
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "#0f1115",
        color: "#f5f5f5",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: 440, width: "100%", textAlign: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{resolvedTitle}</h1>
        <p
          style={{
            marginTop: 12,
            fontSize: 14,
            lineHeight: 1.5,
            color: "#b8bcc4",
          }}
        >
          {resolvedBody}
        </p>
        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            onClick={handleRefresh}
            style={{
              borderRadius: 16,
              border: "none",
              background: "#f5f5f5",
              color: "#0f1115",
              padding: "12px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t("shell.refresh_app")}
          </button>
          <button
            type="button"
            onClick={handleHome}
            style={{
              borderRadius: 16,
              border: "1px solid #2a2e36",
              background: "transparent",
              color: "#f5f5f5",
              padding: "12px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t("shell.go_home")}
          </button>
        </div>
      </div>
    </div>
  );
}
